import React, { useEffect, useState } from "react";
import _ from "lodash";
import firebase, { FS } from "../../../services/firebase";
import { displayDateTime } from "../../../utils/dateDisplay";

// The codes workflow-multi-api writes to notifications.cancelled_reason.
const CANCEL_REASON_TEXT = {
  workflow_archived: "the workflow was archived",
  workflow_deleted: "the workflow was deleted",
  appointment_booked: "an appointment was booked",
};

function buildCancelledAction({ cancelledAt, cancelledReason, cancelledBy }) {
  let action = cancelledAt ? `was cancelled at ${cancelledAt}` : "was cancelled";
  // Own keys only, so an unknown code like "constructor" falls back to no reason.
  const reasonText = _.has(CANCEL_REASON_TEXT, [cancelledReason]) ? CANCEL_REASON_TEXT[cancelledReason] : "";
  if (reasonText) {
    action += ` because ${reasonText}`;
  }
  if (cancelledBy) {
    action += ` (by ${cancelledBy})`;
  }
  return action;
}

function buildLogContent({
  templateId,
  type,
  delivered,
  deliveredAt,
  sendAt,
  cancelled,
  cancelledAt,
  cancelledReason,
  cancelledBy,
}) {
  let target = "notification";
  let what = "";
  let action = "";

  if (["sms1", "sms2"].includes(type)) {
    target = "SMS";
  } else if (["email", "email2"].includes(type)) {
    target = "Email";
  }

  if ((templateId || "").includes("booking_self_service_reminder")) {
    what = "Pre-booking";
  } else if ("patient_booking_return_to_gp" === templateId) {
    what = "Return to GP";
  } else {
    what = templateId;
  }

  if (delivered === true) {
    action = `was delivered at ${deliveredAt}`;
  } else if (cancelled === true) {
    action = buildCancelledAction({ cancelledAt, cancelledReason, cancelledBy });
  } else if (sendAt) {
    action = `is waiting for delivery at ${sendAt}`;
  } else {
    action = "is waiting for delivery";
  }

  return `${what} ${target} ${action}`;
}

const NotificationLogList = ({ caId }) => {
  const [logList, setLogList] = useState([]);

  useEffect(() => {
    const q = FS.query(
      FS.collection(firebase.modularFirestore(), "notifications"),
      FS.where("client_appointment_id", "==", caId),
      FS.orderBy("created_at", "asc")
    );
    const unsubscribe = FS.onSnapshot(q, (querySnapshot) => {
      const logs = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const { created_at, delivered, delivered_at, template_id: templateId, type, send_at } = data;
        const createdAt = displayDateTime(created_at);
        let deliveredAt = null;
        if (delivered && delivered_at) {
          deliveredAt = displayDateTime(delivered_at);
        }
        let sendAt = null;
        if (send_at) {
          sendAt = displayDateTime(send_at);
        }
        const cancelled = _.get(data, "cancelled", false);
        const cancelledAt = displayDateTime(_.get(data, "cancelled_at", null));
        const cancelledReason = _.get(data, "cancelled_reason", "");
        const cancelledBy = _.get(data, "cancelled_by.email", "");
        logs.push({
          id: doc.id,
          createdAt,
          content: buildLogContent({
            templateId,
            type,
            delivered,
            deliveredAt,
            sendAt,
            cancelled,
            cancelledAt,
            cancelledReason,
            cancelledBy,
          }),
        });
      });
      setLogList(logs);
    });
    return () => unsubscribe();
  }, [caId]);

  return (
    <>
      {logList.map((log) => (
        <div key={log.id} className="columns is-1">
          <div className="column is-narrow is-one-quarter is-family-monospace p-0 has-text-grey">{log.createdAt}</div>
          <span className="sms-log column py-0 pr-0 pb-2">{log.content}</span>
        </div>
      ))}
    </>
  );
};

export default NotificationLogList;
